/**
 * Vest NQ-PERP volume as thin bars inside the CME volume pane.
 *
 * A lightweight-charts custom series: each bar is VEST_BAR_WIDTH of the bar
 * spacing wide, centred on the CME bar, so the (wider, translucent) CME bar
 * stays readable on both sides. A bar runs from `base` to `top`: 0..v on
 * its own invisible scale (independent mode), or cmeVol..cmeVol+equivalent
 * on the CME scale (same-units mode, stacked).
 *
 * Colour + pattern, so buy/sell does not rely on hue alone: net buy = solid
 * light amber, net sell = dark amber with diagonal hatching, no split
 * (kline-derived) = amber outline.
 */
import type {
  CustomSeriesOptions,
  CustomSeriesPricePlotValues,
  ICustomSeriesPaneRenderer,
  ICustomSeriesPaneView,
  PaneRendererCustomData,
  PriceToCoordinateConverter,
  Time,
  WhitespaceData,
} from 'lightweight-charts';
import { customSeriesDefaultOptions } from 'lightweight-charts';
import type { VestBarColors, VestBarDatum } from '../../utils/vestVolume';
import { DEFAULT_VEST_BAR_COLORS } from '../../utils/vestVolume';

/** Fraction of the bar spacing a Vest bar takes. */
export const VEST_BAR_WIDTH = 0.4;

export interface VestVolumeSeriesOptions extends CustomSeriesOptions {
  vestColors: VestBarColors;
}

/** Pixel span [left, right) of a bar centred at x (media px). */
export function vestBarSpan(x: number, barSpacing: number): { left: number; width: number } {
  const width = Math.max(1, barSpacing * VEST_BAR_WIDTH);
  return { left: x - width / 2, width };
}

class Renderer implements ICustomSeriesPaneRenderer {
  private _data: PaneRendererCustomData<Time, VestBarDatum> | null = null;
  private _opts: VestVolumeSeriesOptions | null = null;

  update(data: PaneRendererCustomData<Time, VestBarDatum>, opts: VestVolumeSeriesOptions): void {
    this._data = data;
    this._opts = opts;
  }

  draw(target: any, priceToCoordinate: PriceToCoordinateConverter): void {
    const data = this._data;
    const opts = this._opts;
    if (!data || !opts || !data.visibleRange) return;
    const colors = opts.vestColors ?? DEFAULT_VEST_BAR_COLORS;
    target.useBitmapCoordinateSpace((scope: any) => {
      const ctx = scope.context as CanvasRenderingContext2D;
      const hr = scope.horizontalPixelRatio as number;
      const vr = scope.verticalPixelRatio as number;
      ctx.save();
      for (let i = data.visibleRange!.from; i < data.visibleRange!.to; i++) {
        const bar = data.bars[i];
        if (!bar) continue;
        const d = bar.originalData;
        if (typeof d.top !== 'number' || typeof d.base !== 'number') continue;
        const yTop = priceToCoordinate(d.top);
        const yBase = priceToCoordinate(d.base);
        if (yTop === null || yBase === null) continue;
        const { left, width } = vestBarSpan(bar.x, data.barSpacing);
        const x0 = Math.round(left * hr);
        const w = Math.max(1, Math.round(width * hr));
        const y0 = Math.round(Math.min(yTop, yBase) * vr);
        const h = Math.max(1, Math.round(Math.abs(yBase - yTop) * vr));
        if (d.kind === 'none') {
          ctx.strokeStyle = colors.neutral;
          ctx.lineWidth = Math.max(1, hr);
          ctx.strokeRect(x0 + 0.5, y0 + 0.5, Math.max(1, w - 1), Math.max(1, h - 1));
          continue;
        }
        ctx.fillStyle = d.kind === 'sell' ? colors.sell : colors.buy;
        ctx.fillRect(x0, y0, w, h);
        if (d.kind === 'sell' && w >= 3 && h >= 3) {
          // Diagonal hatch: net selling is readable without colour.
          ctx.save();
          ctx.beginPath();
          ctx.rect(x0, y0, w, h);
          ctx.clip();
          ctx.strokeStyle = 'rgba(255, 255, 255, 0.55)';
          ctx.lineWidth = Math.max(1, hr * 0.75);
          const step = Math.max(3, 4 * hr);
          ctx.beginPath();
          for (let k = -h; k < w; k += step) {
            ctx.moveTo(x0 + k, y0 + h);
            ctx.lineTo(x0 + k + h, y0);
          }
          ctx.stroke();
          ctx.restore();
        }
      }
      ctx.restore();
    });
  }
}

export class VestVolumeSeriesView implements ICustomSeriesPaneView<Time, VestBarDatum, VestVolumeSeriesOptions> {
  private _renderer = new Renderer();

  renderer(): ICustomSeriesPaneRenderer {
    return this._renderer;
  }

  update(data: PaneRendererCustomData<Time, VestBarDatum>, options: VestVolumeSeriesOptions): void {
    this._renderer.update(data, options);
  }

  priceValueBuilder(d: VestBarDatum): CustomSeriesPricePlotValues {
    return [d.base, d.top];
  }

  isWhitespace(d: VestBarDatum | WhitespaceData<Time>): d is WhitespaceData<Time> {
    return typeof (d as Partial<VestBarDatum>).top !== 'number';
  }

  defaultOptions(): VestVolumeSeriesOptions {
    return { ...customSeriesDefaultOptions, vestColors: DEFAULT_VEST_BAR_COLORS };
  }
}
